/**
 * 文档管理（旧 views/documents/DocumentManagementView.vue）：筛选、分页、发布开关、预览、上传 / 编辑、删除
 */
import { useEffect, useState } from 'react'
import {
  App,
  Button,
  Card,
  Col,
  Input,
  Popconfirm,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  type TableColumnsType
} from 'antd'
import { DeleteOutlined, EditOutlined, EyeOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons'
import { PageHeader } from '@/components/PageHeader'
import type { MarkdownDocument } from '@/api/services/documents'
import { CATEGORY_OPTIONS, LANGUAGE_OPTIONS, categoryLabel, documentPreviewUrl } from './constants'
import { useDeleteDocument, useDocumentList, useTogglePublish, type DocumentFilters } from './hooks'
import { DocumentFormModal } from './DocumentFormModal'
import styles from './DocumentManagementPage.module.css'

type ModalState = { type: 'create' } | { type: 'edit'; document: MarkdownDocument } | null

const LANGUAGE_FILTER_OPTIONS = [{ value: '', label: '全部语言' }, ...LANGUAGE_OPTIONS]
const CATEGORY_FILTER_OPTIONS = [{ value: '', label: '全部分类' }, ...CATEGORY_OPTIONS]

export function DocumentManagementPage() {
  const { message } = App.useApp()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  // 搜索框输入后按回车或点搜索才提交；下拉框改变立即查询（与旧版一致）
  const [searchText, setSearchText] = useState('')
  const [filters, setFilters] = useState<DocumentFilters>({})
  const [modalState, setModalState] = useState<ModalState>(null)
  // 正在切换发布状态的文档，各自显示加载状态
  const [publishingIds, setPublishingIds] = useState<ReadonlySet<number>>(new Set())
  const { data, isFetching, isError } = useDocumentList(page, pageSize, filters)
  const togglePublish = useTogglePublish()
  const deleteDocument = useDeleteDocument()

  useEffect(() => {
    if (isError) message.error('加载文档列表失败')
  }, [isError, message])

  const applyFilters = (patch: DocumentFilters) => {
    setFilters((current) => ({ ...current, search: searchText, ...patch }))
    setPage(1)
  }

  const handleTogglePublish = async (record: MarkdownDocument, checked: boolean) => {
    setPublishingIds((current) => new Set(current).add(record.id))
    try {
      await togglePublish.mutateAsync({ id: record.id, isPublished: checked })
      message.success(checked ? '文档已发布' : '文档已下线')
    } catch (error) {
      console.error('切换发布状态失败:', error)
      message.error('操作失败')
    } finally {
      setPublishingIds((current) => {
        const next = new Set(current)
        next.delete(record.id)
        return next
      })
    }
  }

  const handleDelete = async (record: MarkdownDocument) => {
    try {
      await deleteDocument.mutateAsync(record.id)
      message.success('文档删除成功')
    } catch (error) {
      console.error('删除文档失败:', error)
      message.error('删除失败')
    }
  }

  // 打开后端渲染的公开页面；未发布的文档后端返回 404，所以只对已发布文档开放
  const handlePreview = (record: MarkdownDocument) => {
    window.open(documentPreviewUrl(record), '_blank', 'noopener,noreferrer')
  }

  const columns: TableColumnsType<MarkdownDocument> = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    { title: 'Slug', dataIndex: 'slug', key: 'slug', width: 150 },
    { title: '标题', dataIndex: 'title', key: 'title', ellipsis: true },
    {
      title: '语言',
      key: 'language',
      width: 100,
      render: (_, record) => (
        <Tag color={record.language === 'zh' ? 'blue' : 'green'}>{record.language === 'zh' ? '中文' : 'English'}</Tag>
      )
    },
    { title: '分类', key: 'category', width: 120, render: (_, record) => <Tag>{categoryLabel(record.category)}</Tag> },
    {
      title: '是否发布',
      key: 'is_published',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <Switch
          aria-label={`发布 ${record.slug}`}
          checked={record.is_published}
          loading={publishingIds.has(record.id)}
          onChange={(checked) => void handleTogglePublish(record, checked)}
        />
      )
    },
    {
      title: '更新时间',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 180,
      render: (value: string) => new Date(value).toLocaleString('zh-CN')
    },
    {
      title: '操作',
      key: 'action',
      width: 240,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title={record.is_published ? undefined : '发布后可预览'}>
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              disabled={!record.is_published}
              onClick={() => handlePreview(record)}
            >
              预览
            </Button>
          </Tooltip>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => setModalState({ type: 'edit', document: record })}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个文档吗？"
            okText="确定"
            cancelText="取消"
            onConfirm={() => handleDelete(record)}
          >
            <Button type="link" danger size="small" icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  return (
    <div>
      <PageHeader
        title="文档管理"
        subtitle="管理隐私政策、用户协议等 Markdown 文档"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalState({ type: 'create' })}>
            上传新文档
          </Button>
        }
      />

      <Card variant="borderless" className={styles.filterCard}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Select
              aria-label="选择语言"
              placeholder="选择语言"
              allowClear
              style={{ width: '100%' }}
              value={filters.language}
              options={LANGUAGE_FILTER_OPTIONS}
              onChange={(value) => applyFilters({ language: value })}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              aria-label="选择分类"
              placeholder="选择分类"
              allowClear
              style={{ width: '100%' }}
              value={filters.category}
              options={CATEGORY_FILTER_OPTIONS}
              onChange={(value) => applyFilters({ category: value })}
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Input
              aria-label="搜索标题或slug"
              placeholder="搜索标题或slug"
              suffix={<SearchOutlined />}
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              onPressEnter={() => applyFilters({})}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Button type="primary" block icon={<SearchOutlined />} onClick={() => applyFilters({})}>
              搜索
            </Button>
          </Col>
        </Row>
      </Card>

      <Card variant="borderless">
        <Table<MarkdownDocument>
          rowKey="id"
          columns={columns}
          dataSource={data?.documents}
          loading={isFetching}
          scroll={{ x: 1100 }}
          pagination={{
            current: page,
            pageSize,
            total: data?.total ?? 0,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条记录`
          }}
          onChange={(pagination) => {
            setPage(pagination.current ?? 1)
            setPageSize(pagination.pageSize ?? 20)
          }}
        />
      </Card>

      <DocumentFormModal
        open={modalState !== null}
        editingDoc={modalState?.type === 'edit' ? modalState.document : null}
        onClose={() => setModalState(null)}
      />
    </div>
  )
}
